import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserOutlined, 
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { actionApi } from '../../utils/actionApi';
import { getSuccessMessage } from '../../utils/actionMessageTranslator';
import { message, Input, Button, Select, Card, Statistic, Modal } from 'antd';
import { ActionCard, FilterPanel } from '../../components/action';
import { CardListSkeleton } from '../../components/SkeletonLoaders';
import { toGregorian } from '../../utils/dateUtils';

const { Search } = Input;

const MyActions = () => {
  const navigate = useNavigate();
  const { actionToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [actions, setActions] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    type: '',
    raci: '',
    due_from: '',
    due_to: '',
    search: '',
    sort: 'due_at',
    dir: 'asc',
    saved_filter_id: undefined,
    bookmarked: undefined,
    requester_id: '',
  });
  const searchTimeoutRef = useRef(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
  });
  const [acceptingId, setAcceptingId] = useState(null);
  const [requestClosingId, setRequestClosingId] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [requestCloseModalOpen, setRequestCloseModalOpen] = useState(false);
  const [requestCloseActionId, setRequestCloseActionId] = useState(null);
  const [requestCloseComment, setRequestCloseComment] = useState('');
  const [bookmarkingId, setBookmarkingId] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const usersSearchTimeoutRef = useRef(null);

  useEffect(() => {
    if (actionToken) {
      fetchMyActions(1);
    }
  }, [actionToken]);

  const fetchMyActions = async (page = 1) => {
    if (!actionToken) return;

    setLoading(true);
    try {
      const params = {
        page,
        per_page: pagination.per_page,
      };

      // اضافه کردن فیلترها (می‌توانند آرایه یا رشته باشند)
      // actionApi.getMyActions خودش منطق تبدیل به آرایه یا رشته جدا شده با کاما را دارد
      if (filters.status) {
        params.status = filters.status;
      }
      if (filters.priority) {
        params.priority = filters.priority;
      }
      if (filters.type) {
        params.type = filters.type;
      }
      if (filters.raci) {
        params.raci = filters.raci;
      }
      if (filters.due_from) {
        const gregorian = toGregorian(filters.due_from, 'YYYY-MM-DD');
        if (gregorian) params.due_from = gregorian;
      }
      if (filters.due_to) {
        const gregorian = toGregorian(filters.due_to, 'YYYY-MM-DD');
        if (gregorian) params.due_to = gregorian;
      }
      if (filters.search) params.search = filters.search;
      if (filters.sort) params.sort = filters.sort;
      if (filters.dir) params.dir = filters.dir;
      if (filters.saved_filter_id !== undefined && filters.saved_filter_id !== null && filters.saved_filter_id !== '') {
        params.saved_filter_id = Number(filters.saved_filter_id);
      }
      if (filters.bookmarked !== undefined) params.bookmarked = filters.bookmarked;
      if (filters.requester_id) params.requester_id = Number(filters.requester_id);

      const res = await actionApi.getMyActions(params);
      const list = res?.data?.data || [];
      const pag = res?.data?.pagination || pagination;
      const statsData = res?.data?.stats || null;
      
      setActions(list);
      setPagination(pag);
      setStats(statsData);
    } catch (error) {
      console.error('Load my actions error:', error);
      message.error('خطا در دریافت لیست اقدام‌های من');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchMyActions(pagination.current_page || 1);
  };

  const handleCardClick = (action) => {
    navigate(`/action/${action.id}`);
  };

  const handleDelete = async (id) => {
    try {
      const res = await actionApi.deleteAction(id);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'اقدام حذف شد'));
        fetchMyActions(pagination.current_page || 1);
      } else {
        message.error(res?.message || 'خطا در حذف اقدام');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در حذف اقدام');
    }
  };

  const isEmpty = !loading && (!actions || actions.length === 0);

  const actionTypes = [
    { value: '', label: 'همه نوع‌ها' },
    { value: 'اصلاح', label: 'اصلاح' },
    { value: 'اقدام_پیشگیرانه', label: 'اقدام پیشگیرانه' },
    { value: 'اقدام_اصلاحی', label: 'اقدام اصلاحی' },
  ];

  const statuses = [
    { value: '', label: 'همه وضعیت‌ها' },
    { value: 'باز', label: 'باز' },
    { value: 'در_حال_برنامه_ریزی', label: 'در حال برنامه‌ریزی' },
    { value: 'در_حال_انجام', label: 'در حال انجام' },
    { value: 'بسته', label: 'بسته' },
  ];

  const priorities = [
    { value: '', label: 'همه اولویت‌ها' },
    { value: 'فوری_و_اضطراری', label: 'فوری و اضطراری' },
    { value: 'بسیار_مهم', label: 'بسیار مهم' },
    { value: 'مهم', label: 'مهم' },
    { value: 'کم_اهمیت', label: 'کم اهمیت' },
    { value: 'غیرضروری', label: 'غیرضروری' },
  ];

  const raciRoles = [
    { value: '', label: 'همه نقش‌ها' },
    { value: 'پاسخگو', label: 'پاسخگو' },
    { value: 'مسئول_اجرا', label: 'مسئول اجرا' },
    { value: 'مطلع', label: 'مطلع' },
    { value: 'مشاور', label: 'مشاور' },
  ];

  const sortOptions = [
    { value: 'due_at', label: 'موعد' },
    { value: 'priority', label: 'اولویت' },
    { value: 'updated_at', label: 'آخرین به‌روزرسانی' },
    { value: 'created_at', label: 'تاریخ ایجاد' },
    { value: 'status', label: 'وضعیت' },
    { value: 'title', label: 'عنوان' },
  ];

  const dirOptions = [
    { value: 'asc', label: 'صعودی' },
    { value: 'desc', label: 'نزولی' },
  ];

  const bookmarkedOptions = [
    { value: 1, label: 'فقط نشان‌گذاری شده' },
    { value: 0, label: 'بدون نشان' },
  ];

  const perPageOptions = [10, 15, 20, 30, 50];

  const setFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // فیلتر خودکار با debounce برای همه فیلترها
  useEffect(() => {
    if (!actionToken) return;
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchMyActions(1);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, actionToken]);

  const resetFilters = () => {
    setFilters({
      status: '',
      priority: '',
      type: '',
      raci: '',
      due_from: '',
      due_to: '',
      search: '',
      sort: 'due_at',
      dir: 'asc',
      saved_filter_id: undefined,
      bookmarked: undefined,
      requester_id: '',
    });
    setPagination(prev => ({ ...prev, per_page: 20 }));
  };

  // Handle real-time filter changes
  const handleQuickFilterChange = (key, value) => {
    setFilter(key, value);
  };

  // دریافت لیست کاربران برای فیلتر درخواست‌کننده
  const fetchUsers = async (search = '') => {
    if (!actionToken) return;
    
    setUsersLoading(true);
    try {
      const res = await actionApi.getUsers({ search, per_page: 50 });
      const usersList = res?.data?.data || [];
      setUsers(
        usersList.map((user) => ({
          value: user.id,
          label: `${user.name || 'بدون نام'}${user.email ? ` - ${user.email}` : ''}`,
          user: user,
        }))
      );
    } catch (error) {
      console.error('Load users error:', error);
      message.error('خطا در دریافت لیست کاربران');
    } finally {
      setUsersLoading(false);
    }
  };

  // Handle search in users select
  const handleUsersSearch = (value) => {
    if (usersSearchTimeoutRef.current) {
      clearTimeout(usersSearchTimeoutRef.current);
    }
    
    usersSearchTimeoutRef.current = setTimeout(() => {
      fetchUsers(value);
    }, 300);
  };

  // Load users on mount
  useEffect(() => {
    if (actionToken) {
      fetchUsers();
    }
    return () => {
      if (usersSearchTimeoutRef.current) {
        clearTimeout(usersSearchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionToken]);

  const handleAccept = async (id) => {
    setAcceptingId(id);
    try {
      const res = await actionApi.acceptAction(id, '');
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'اقدام پذیرفته شد'));
        fetchMyActions(pagination.current_page || 1);
      } else {
        message.error(res?.message || 'خطا در پذیرش اقدام');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در پذیرش اقدام');
    } finally {
      setAcceptingId(null);
    }
  };

  const handleRequestCloseClick = (id) => {
    setRequestCloseActionId(id);
    setRequestCloseComment('');
    setRequestCloseModalOpen(true);
  };

  const handleRequestClose = async () => {
    if (!requestCloseActionId) return;
    
    setRequestClosingId(requestCloseActionId);
    try {
      const comment = requestCloseComment.trim() || '';
      const res = await actionApi.requestCloseAction(requestCloseActionId, comment);
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'درخواست بستن ارسال شد'));
        setRequestCloseModalOpen(false);
        setRequestCloseComment('');
        setRequestCloseActionId(null);
        fetchMyActions(pagination.current_page || 1);
      } else {
        message.error(res?.message || 'خطا در ارسال درخواست بستن');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در ارسال درخواست بستن');
    } finally {
      setRequestClosingId(null);
    }
  };

  const handleClose = async (id) => {
    setClosingId(id);
    try {
      const res = await actionApi.closeAction(id, '');
      if (res?.success) {
        message.success(getSuccessMessage(res.message, 'اقدام بسته شد'));
        fetchMyActions(pagination.current_page || 1);
      } else {
        message.error(res?.message || 'خطا در بستن اقدام');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در بستن اقدام');
    } finally {
      setClosingId(null);
    }
  };

  const handleBookmark = async (id) => {
    setBookmarkingId(id);
    try {
      const res = await actionApi.toggleActionBookmark(id);
      if (res?.success) {
        // به‌روزرسانی وضعیت بوکمارک در لیست محلی
        setActions(prevActions => 
          prevActions.map(action => 
            action.id === id 
              ? { ...action, is_bookmarked: !action.is_bookmarked }
              : action
          )
        );
        message.success(getSuccessMessage(res.message, 'وضعیت بوکمارک تغییر کرد'));
      } else {
        message.error(res?.message || 'خطا در تغییر وضعیت بوکمارک');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || 'خطا در تغییر وضعیت بوکمارک');
    } finally {
      setBookmarkingId(null);
    }
  };

  return (
    <MainLayout>
      <div className="px-4 md:px-6 py-5 w-full">
        {/* هدر */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <UserOutlined className="text-white text-xl" />
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white dark:border-gray-900" />
              </div>
              <div>
                <h1
                  className="text-2xl font-bold text-gray-900 dark:text-white"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  اقدام‌های من
                </h1>
                <p
                  className="text-sm text-gray-500 dark:text-gray-400 mt-0.5"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  {pagination.total > 0 ? `${pagination.total} اقدام مرتبط با شما` : 'لیست اقدام‌های مرتبط با شما'}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <button
                onClick={handleRefresh}
                className="px-4 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center justify-center gap-2 transition-colors"
                style={{ fontFamily: 'CustomFont' }}
              >
                <ReloadOutlined className="text-sm" />
                به‌روزرسانی
              </button>
              <button
                onClick={() => navigate('/action/create')}
                className="px-5 py-2 text-sm rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25 transition-all"
                style={{ fontFamily: 'CustomFont' }}
              >
                <PlusOutlined className="text-sm" />
                اقدام جدید
              </button>
            </div>
          </div>

          {/* آمار کلی */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <Card size="small" style={{ borderRadius: 12, background: '#f5f3ff' }} bodyStyle={{ padding: 12 }}>
                <Statistic
                  title={<span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>کل</span>}
                  value={stats.total || 0}
                  valueStyle={{ fontSize: 18, fontFamily: 'CustomFont', color: '#8b5cf6' }}
                />
              </Card>
              <Card size="small" style={{ borderRadius: 12, background: '#fef3c7' }} bodyStyle={{ padding: 12 }}>
                <Statistic
                  title={<span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>در انتظار</span>}
                  value={stats.pending || 0}
                  valueStyle={{ fontSize: 18, fontFamily: 'CustomFont', color: '#f59e0b' }}
                />
              </Card>
              <Card size="small" style={{ borderRadius: 12, background: '#dbeafe' }} bodyStyle={{ padding: 12 }}>
                <Statistic
                  title={<span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>در حال انجام</span>}
                  value={stats.in_progress || 0}
                  valueStyle={{ fontSize: 18, fontFamily: 'CustomFont', color: '#3b82f6' }}
                />
              </Card>
              <Card size="small" style={{ borderRadius: 12, background: '#d1fae5' }} bodyStyle={{ padding: 12 }}>
                <Statistic
                  title={<span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>تکمیل شده</span>}
                  value={stats.closed || 0}
                  valueStyle={{ fontSize: 18, fontFamily: 'CustomFont', color: '#10b981' }}
                />
              </Card>
              <Card size="small" style={{ borderRadius: 12, background: '#fee2e2' }} bodyStyle={{ padding: 12 }}>
                <Statistic
                  title={<span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>تأخیر خورده</span>}
                  value={stats.overdue || 0}
                  valueStyle={{ fontSize: 18, fontFamily: 'CustomFont', color: '#ef4444' }}
                />
              </Card>
            </div>
          )}

          {/* فیلترهای سریع (جستجو، نقش RACI، درخواست‌کننده) */}
         

          {/* پنل فیلتر */}
          <FilterPanel
            filters={{
              status: filters.status,
              priority: filters.priority,
              action_type: filters.type,
              due_from: filters.due_from,
              due_to: filters.due_to,
              sort: filters.sort,
              dir: filters.dir,
              saved_filter_id: filters.saved_filter_id,
              bookmarked: filters.bookmarked,
              per_page: pagination.per_page,
            }}
            onFilterChange={(key, value) => {
              if (key === 'action_type') {
                setFilter('type', value);
              } else if (key === 'per_page') {
                setPagination(prev => ({ ...prev, per_page: value }));
              } else {
                setFilter(key, value);
              }
            }}
            onApply={() => {}}
            onReset={resetFilters}
            filterOptions={{
              statuses,
              priorities,
              actionTypes,
              sortOptions,
              dirOptions,
              bookmarkedOptions,
              perPageOptions,
            }}
            defaultExpanded={false}
          />
        </div>
        <div className="mb-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* جستجو */}
              <div>
                <label
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  جستجو
                </label>
                <Input
                  placeholder=""
                  value={filters.search || ''}
                  onChange={(e) => handleQuickFilterChange('search', e.target.value)}
                  allowClear
                  style={{ fontFamily: 'CustomFont' }}
                />
              </div>

              {/* نقش RACI */}
              <div>
                <label
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  نقش RACI
                </label>
                <Select
                  mode="multiple"
                  placeholder="همه نقش‌ها"
                  value={Array.isArray(filters.raci) ? filters.raci : filters.raci ? [filters.raci] : []}
                  onChange={(val) => handleQuickFilterChange('raci', val && val.length > 0 ? val : '')}
                  allowClear
                  style={{ width: '100%', fontFamily: 'CustomFont' }}
                  options={raciRoles.filter(r => r.value).map((r) => ({
                    value: r.value,
                    label: r.label,
                  }))}
                />
              </div>

              {/* درخواست‌کننده */}
              <div>
                <label
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  درخواست‌کننده
                </label>
                <Select
                  showSearch
                  placeholder="انتخاب درخواست‌کننده..."
                  value={filters.requester_id ? Number(filters.requester_id) : undefined}
                  onChange={(val) => handleQuickFilterChange('requester_id', val ? String(val) : '')}
                  onSearch={handleUsersSearch}
                  filterOption={false}
                  loading={usersLoading}
                  allowClear
                  style={{ width: '100%', fontFamily: 'CustomFont' }}
                  options={users}
                  notFoundContent={usersLoading ? 'در حال جستجو...' : 'نتیجه‌ای یافت نشد'}
                />
              </div>
            </div>
          </div>

        {/* بدنه لیست */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
          {loading && (
            <div className="p-6">
              <CardListSkeleton count={5} />
            </div>
          )}

          {!loading && isEmpty && (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="relative mb-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 flex items-center justify-center">
                  <UserOutlined className="text-gray-400 text-2xl" />
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">!</span>
                </div>
              </div>
              <p
                className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-1"
                style={{ fontFamily: 'CustomFont' }}
              >
                {filters.search ? 'نتیجه‌ای یافت نشد' : 'هیچ اقدامی مرتبط با شما یافت نشد'}
              </p>
              <p
                className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center max-w-md"
                style={{ fontFamily: 'CustomFont' }}
              >
                {filters.search 
                  ? 'لطفاً عبارت جستجوی دیگری را امتحان کنید'
                  : 'اقدام‌هایی که به شما اختصاص داده شده یا در آن‌ها نقش دارید در اینجا نمایش داده می‌شوند'}
              </p>
              {!filters.search && (
                <button
                  onClick={() => navigate('/action/create')}
                  className="px-5 py-2.5 text-sm rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white flex items-center gap-2 shadow-lg shadow-emerald-500/25 transition-all"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  <PlusOutlined className="text-sm" />
                  ساخت اقدام جدید
                </button>
              )}
            </div>
          )}

          {!loading && !isEmpty && (
            <div className="space-y-3 p-4">
              {actions.map((action, index) => {
                const roles = Array.isArray(action.my_role) ? action.my_role : [];

                return (
                  <div key={action.id} className="relative">
                    <ActionCard
                      action={action}
                      onClick={handleCardClick}
                      onDelete={action.can_delete !== false ? (action) => handleDelete(action.id) : undefined}
                      isBookmarked={!!action.is_bookmarked}
                      onBookmark={() => handleBookmark(action.id)}
                      bookmarkLoading={bookmarkingId === action.id}
                      myRoles={roles}
                      onAccept={roles.includes('مسئول_اجرا') && action.is_accepted === false ? () => handleAccept(action.id) : undefined}
                      onRequestClose={roles.includes('مسئول_اجرا') && action.has_close_request === false ? () => handleRequestCloseClick(action.id) : undefined}
                      onClose={roles.includes('پاسخگو') ? () => handleClose(action.id) : undefined}
                      acceptLoading={acceptingId === action.id}
                      requestCloseLoading={requestClosingId === action.id}
                      closeLoading={closingId === action.id}
                      className="animate-fadeIn"
                      style={{
                        animationDelay: `${index * 0.05}s`,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* صفحه‌بندی */}
        {!loading && !isEmpty && pagination.last_page > 1 && (
          <div className="mt-6">
            <div className="flex items-center justify-center">
              <div className="max-w-full overflow-x-auto">
                <div className="inline-flex items-center justify-center gap-2 px-1">
                  {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => fetchMyActions(page)}
                      className={`w-10 h-10 rounded-xl text-sm font-medium transition-all flex items-center justify-center ${
                        page === pagination.current_page
                          ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                          : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                      style={{ fontFamily: 'CustomFont' }}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out both;
        }
      `}</style>

      {/* Modal درخواست بستن */}
      <Modal
        title="درخواست بستن اقدام"
        open={requestCloseModalOpen}
        onOk={handleRequestClose}
        onCancel={() => {
          setRequestCloseModalOpen(false);
          setRequestCloseComment('');
          setRequestCloseActionId(null);
        }}
        okText="ارسال درخواست"
        cancelText="انصراف"
        confirmLoading={requestClosingId !== null}
        okButtonProps={{
          className: 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 border-none',
        }}
        style={{ fontFamily: 'CustomFont' }}
      >
        <div>
          <p style={{ fontFamily: 'CustomFont', marginBottom: 12, color: '#6b7280' }}>
            توضیحات درخواست بستن (اختیاری)
          </p>
          <Input.TextArea
            value={requestCloseComment}
            onChange={(e) => setRequestCloseComment(e.target.value)}
            placeholder="مثلاً: کار به اتمام رسیده است. لطفاً اقدام را ببندید."
            rows={4}
            maxLength={1000}
            showCount
            style={{ fontFamily: 'CustomFont', borderRadius: 12 }}
          />
        </div>
      </Modal>
    </MainLayout>
  );
};

export default MyActions;
